import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { BudgetProgressView } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { money, payeeName } from "../../shared/lib/format";
import { Modal } from "../../shared/ui/Modal";
import { excludeFromBudget, includeInBudget } from "./api";

interface BudgetTransactionsDialogProps {
  open: boolean;
  onClose: () => void;
  bookID: string;
  view: BudgetProgressView;
  onNotify: (message: string) => void;
  onChanged: () => Promise<void>;
}

// A budget's total is only trustworthy if the facts behind it can be read and
// corrected. A category is a blunt instrument: one reimbursed purchase should
// not force a recategorisation of a journal entry to stop counting against a
// limit. Excluding one leaves the journal, the category and the list alone; it
// only stops that transaction counting here.
export function BudgetTransactionsDialog({ open, onClose, bookID, view, onNotify, onChanged }: BudgetTransactionsDialogProps) {
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState("");
  const rows = view.transactions ?? [];

  const toggle = useMutation({
    mutationFn: ({ transactionID, exclude }: { transactionID: string; exclude: boolean }) => (exclude
      ? excludeFromBudget(bookID, view.budget.id, transactionID)
      : includeInBudget(bookID, view.budget.id, transactionID)),
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["planning"] });
      await onChanged();
      onNotify(variables.exclude
        ? "Excluded from this budget. The journal entry and its category are unchanged."
        : "Counted in this budget again.");
    },
    onError: (error) => setFailure(humanError(error)),
  });

  const excluded = rows.filter((row) => row.excluded).length;
  const planned = rows.filter((row) => !row.excluded && row.planned).length;

  return (
    <Modal
      open={open} onClose={onClose} title="What this budget counts" subtitle={view.budget.name}
      footer={<button className="secondary-button" type="button" onClick={onClose}>Done</button>}
    >
      <p className="panel-note">
        Every transaction this budget's categories caught.{" "}
        {view.budget.scope === "general"
          ? "This budget is one ceiling over its categories, so everything here counts, the spending your plans own included."
          : "This budget limits one thing, so spending a plan item or group already owns is that plan's business and is listed without counting."}{" "}
        Excluding one of the rest stops it counting against this limit only: its journal entry, its category and every
        other figure stay exactly as they are.
      </p>
      <dl className="pick-total">
        <div><dt>Counted</dt><dd>{money(view.spentBase.minor, view.spentBase.currency)}</dd></div>
        <div><dt>Transactions</dt><dd>{rows.length - excluded - planned} of {rows.length}</dd></div>
        {planned > 0 ? <div><dt>Owned by a plan</dt><dd>{planned}</dd></div> : null}
      </dl>
      {rows.length === 0 ? (
        <p className="panel-note">Nothing in these categories yet this period.</p>
      ) : (
        <ul className="pick-list">
          {rows.map((row) => (
            <li key={row.transactionId} className={`pick-row ${row.excluded || row.planned ? "excluded" : ""}`}>
              <span className="pick-main readonly">
                <span className="pick-copy">
                  <strong title={row.description}>{payeeName(row.description || "Transaction")}</strong>
                  <small>
                    {new Date(row.effectiveAt).toLocaleDateString("en", { day: "numeric", month: "short" })}
                    {row.planned ? ` · counted by the plan ${row.planned.type === "group" ? "group" : "item"} “${row.planned.name}”` : ""}
                    {row.excluded ? ` · not counted${row.exclusionNote ? `: ${row.exclusionNote}` : ""}` : ""}
                  </small>
                </span>
                <span className="pick-amount">{money(row.bookValue.minor, row.bookValue.currency)}</span>
                {row.planned ? (
                  <span className="chip" title="A plan already accounts for this money; the budget would charge it twice.">Planned</span>
                ) : (
                  <button className={`chip ${row.excluded ? "" : "danger"}`} type="button" disabled={toggle.isPending}
                    onClick={() => { setFailure(""); toggle.mutate({ transactionID: row.transactionId, exclude: !row.excluded }); }}>
                    {row.excluded ? "Count it" : "Exclude"}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {failure ? <p className="form-error" role="alert">{failure}</p> : null}
    </Modal>
  );
}
